import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { SeoHead } from '@/hooks/usePageSEO';
import { buildImageAlt } from '@/utils/imageSeo';
import { 
  MapPin, Calendar, Home, Phone, Mail, Heart, Shield, Trophy, Award,
  MessageCircle, Star, ChevronRight, ExternalLink, Check, Lock, User,
  Baby, Dog, Clock, DollarSign, Camera, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { WhatsAppButton, CallButton, FacebookShareButton, QRCodeButton } from '@/components/SocialShare';
import { usePageViews } from '@/hooks/usePageViews';
import ApisAIFooter from '@/components/ApisAIFooter';
import ViewCounter from '@/components/ViewCounter';

import { API } from '@/utils/api';
const BASE_URL = 'https://apisai.pl';

// Real View Counter Badge
const ViewCounterBadge = ({ pageType, pageSlug }) => {
  const { totalViews, weeklyViews, isLoading } = usePageViews(pageType, pageSlug);
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  
  if (!show || isLoading || (totalViews === 0 && weeklyViews === 0)) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-4 left-4 z-50 bg-slate-900/95 backdrop-blur-xl border border-emerald-500/30 rounded-2xl px-4 py-3 shadow-lg"
    >
      <div className="flex items-center gap-3 text-sm">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
          <Eye className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">{totalViews}</span>
            <span className="text-gray-400">wyświetleń</span>
          </div>
          {weeklyViews > 0 && (
            <p className="text-emerald-400 text-xs">
              +{weeklyViews} w tym tygodniu
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Helper to get full image URL
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/utils/imageUrl';
import { useTranslation } from 'react-i18next';

// Placeholder image
const PLACEHOLDER = PLACEHOLDER_IMAGE;

// Gold gradient text
const GoldText = ({ children, className = "" }) => (
  <span className={`bg-clip-text text-transparent bg-gradient-to-r from-[#D4AF37] via-[#F5DEB3] to-[#D4AF37] ${className}`}>
    {children}
  </span>
);

// Badge component
const Badge = ({ icon: Icon, children, color = "gold" }) => {
  const colors = {
    gold: "border-[#D4AF37]/30 text-[#D4AF37] bg-[#D4AF37]/5",
    green: "border-emerald-500/30 text-emerald-400 bg-emerald-500/5",
    blue: "border-blue-500/30 text-blue-400 bg-blue-500/5",
    pink: "border-pink-500/30 text-pink-400 bg-pink-500/5",
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium backdrop-blur-sm ${colors[color]}`}>
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </div>
  );
};

// Glass card
const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-[#10182B]/60 backdrop-blur-md border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors ${className}`}>
    {children}
  </div>
);

// Empty state component
const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-12 px-6 bg-white/5 rounded-2xl border border-white/10">
    {Icon && <Icon className="w-12 h-12 text-gray-600 mx-auto mb-4" />}
    <h3 className="text-lg font-medium text-gray-400 mb-2">{title}</h3>
    {description && <p className="text-gray-500 text-sm">{description}</p>}
  </div>
);

// Parent card component
const ParentCard = ({ parent, gender, linkTo, linkLabel }) => {
  const isValid = parent?.name || parent?.photo;
  if (!isValid) return null;

  const borderColor = gender === 'male' ? 'border-blue-500/30 hover:border-blue-500/50' : 'border-pink-500/30 hover:border-pink-500/50';
  const iconColor = gender === 'male' ? 'text-blue-400' : 'text-pink-400';
  const bgColor = gender === 'male' ? 'bg-blue-500/10' : 'bg-pink-500/10';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`rounded-2xl border ${borderColor} overflow-hidden bg-[#10182B]/60 backdrop-blur-md transition-all`}
    >
      {/* Photo */}
      <div className="relative aspect-square overflow-hidden">
        <img 
          src={getImageUrl(parent.photo) || PLACEHOLDER}
          alt={parent.name || 'Rodzic'}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F1A] via-transparent to-transparent" />
        
        {/* Gender badge */}
        <div className={`absolute top-4 left-4 w-10 h-10 rounded-full ${bgColor} flex items-center justify-center`}>
          <span className={`text-xl ${iconColor}`}>{gender === 'male' ? '♂' : '♀'}</span>
        </div>
      </div>

      {/* Info */}
      <div className="p-6">
        <p className={`text-sm font-medium uppercase tracking-wider mb-1 ${iconColor}`}>
          {gender === 'male' ? 'Ojciec' : 'Matka'}
        </p>
        <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'var(--pub-head)' }}>
          {parent.name || '—'}
        </h3>
        {parent.titles && (
          <p className="text-gray-400 text-sm mb-4">{parent.titles}</p>
        )}

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {parent.championships && <Badge icon={Trophy} color={gender === 'male' ? 'blue' : 'pink'}>Champion</Badge>}
          {parent.health && <Badge icon={Shield} color="green">Badania OK</Badge>}
          {parent.training && <Badge icon={Award}>Wyszkolony</Badge>}
        </div>

        {/* Description */}
        {parent.description && (
          <p className="text-gray-400 text-sm line-clamp-3 mb-4">{parent.description}</p>
        )}

        {/* Link */}
        {linkTo && (
          <Link to={linkTo}>
            <Button 
              variant="outline" 
              className="w-full border-white/20 text-white hover:bg-white/5"
            >
              {linkLabel || 'Zobacz profil'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </div>
    </motion.div>
  );
};

// Puppy card component
const PuppyCard = ({ puppy, index, onReserve, isPremium }) => {
  const statusColors = {
    available: 'border-emerald-500/50 bg-emerald-500/10',
    reserved: 'border-amber-500/50 bg-amber-500/10',
    sold: 'border-gray-500/50 bg-gray-500/10',
  };
  const statusLabels = {
    available: 'Dostępny',
    reserved: 'Zarezerwowany',
    sold: 'Sprzedany',
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-xl border ${statusColors[puppy.status] || statusColors.available} overflow-hidden`}
    >
      {/* Photo */}
      <div className="relative aspect-square overflow-hidden">
        <img 
          src={getImageUrl(puppy.photo) || PLACEHOLDER}
          alt={puppy.name || `Szczeniak ${index + 1}`}
          className="w-full h-full object-cover"
        />
        
        {/* Status badge */}
        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
          puppy.status === 'available' ? 'bg-emerald-500 text-white' :
          puppy.status === 'reserved' ? 'bg-amber-500 text-black' :
          'bg-gray-500 text-white'
        }`}>
          {statusLabels[puppy.status] || 'Dostępny'}
        </div>

        {/* Gender */}
        <div className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center ${
          puppy.sex === 'male' ? 'bg-blue-500/80' : 'bg-pink-500/80'
        }`}>
          <span className="text-white text-sm">{puppy.sex === 'male' ? '♂' : '♀'}</span>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-white font-medium text-sm">{puppy.name || `Szczeniak ${index + 1}`}</p>
        {puppy.collar_color && (
          <p className="text-gray-400 text-xs">Obroża: {puppy.collar_color}</p>
        )}
        
        {/* Reserve button */}
        {puppy.status === 'available' && isPremium && (
          <Button 
            size="sm" 
            className="w-full mt-2 bg-[#D4AF37] hover:bg-[#B4941F] text-black text-xs"
            onClick={() => onReserve && onReserve(puppy)}
          >
            Zarezerwuj
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export default function PublicLitter() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { totalViews, lastView } = usePageViews('litter', slug);
  const [litter, setLitter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchLitter = async () => {
      try {
        const res = await axios.get(`${API}/public/miot/${slug}`);
        // API returns {litter, kennel, seo}
        const data = res.data;
        setLitter({
          ...data.litter,
          kennel_name: data.kennel?.name,
          kennel_slug: data.kennel?.slug,
          contact_phone: data.kennel?.phone || data.litter?.contact_phone,
          contact_email: data.kennel?.email || data.litter?.contact_email,
        });
      } catch (e) {
        setError('Nie znaleziono miotu');
      } finally {
        setLoading(false);
      }
    };
    fetchLitter();
  }, [slug]);

  // Handle contact form submit
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email) {
      toast.error('Wypełnij wymagane pola');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        litter_id: litter.id,
        ...contactForm,
        message: contactForm.message || `Interesuję się szczeniakiem z miotu ${litter.name}`
      });
      toast.success('Wiadomość wysłana! Skontaktujemy się wkrótce.');
      setShowContactForm(false);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (e) {
      toast.error('Błąd wysyłania. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !litter) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Nie znaleziono miotu</h1>
          <Link to="/" className="text-[#D4AF37] hover:underline">Wróć do strony głównej</Link>
        </div>
      </div>
    );
  }

  // Calculate dates
  const birthDate = litter.birth_date ? new Date(litter.birth_date) : null;
  const readyDate = litter.pickup_date ? new Date(litter.pickup_date) : null;
  const isPlanned = litter.status === 'planned';
  const isPremium = true; // TODO: check actual plan

  // Get hero image
  const heroImage = getImageUrl(litter.cover_image) || (litter.gallery?.length > 0 ? getImageUrl(litter.gallery[0]) : PLACEHOLDER);

  // Parents data
  const father = {
    name: litter.father_name,
    photo: litter.father_photo,
    titles: litter.father_titles,
    championships: litter.father_titles?.includes('Champion'),
    health: true,
  };
  const mother = {
    name: litter.mother_name,
    photo: litter.mother_photo,
    titles: litter.mother_titles,
    championships: litter.mother_titles?.includes('Champion'),
    health: true,
  };

  // Available puppies count
  const totalPuppies = litter.puppy_count_male + litter.puppy_count_female || litter.puppies_count || 0;
  const availablePuppies = litter.available_count || litter.available_puppies || totalPuppies;

  // Price display
  const priceDisplay = litter.price || litter.price_info || 'Do ustalenia';

  // SEO data
  const seoTitle = `${litter.breed} - Szczenięta z hodowli ${litter.kennel_name || ''} | ApisAI`;
  const _litterDescBuilt = `Miot ${litter.breed} - ${availablePuppies} szczeniąt dostępnych.${litter.city ? ` Lokalizacja: ${litter.city}.` : ''} Sprawdź rodziców i zarezerwuj szczeniaka.`;
  const _litterDescRaw = (litter.description || '').trim();
  const seoDescription = _litterDescRaw.length >= 50 ? _litterDescRaw.slice(0, 160) : _litterDescBuilt;
  const seoImage = litter.gallery?.[0] ? getImageUrl(litter.gallery[0]) : null;
  const heroAlt = buildImageAlt({ kind: 'litter', name: litter.name, breed: litter.breed, city: litter.city });
  const fontTypo = litter.font_style === 'modern' ? 'pub-typo-modern' : 'pub-typo-elegant';
  const footerTheme = { bg: 'bg-[#0B0F1A]', border: 'border-white/10', text: 'text-white', textMuted: 'text-white/60' };
  const imageSchema = seoImage ? {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": seoImage,
    "url": seoImage,
    "caption": heroAlt,
    "representativeOfPage": true,
  } : null;
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `Szczenięta ${litter.breed}`,
    "description": seoDescription,
    "brand": { "@type": "Brand", "name": litter.kennel_name },
    "category": "Szczenięta",
    "image": seoImage,
    "offers": {
      "@type": "AggregateOffer",
      "availability": availablePuppies > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "priceCurrency": "PLN",
      "lowPrice": litter.price_min || "0",
      "highPrice": litter.price_max || litter.price || "0",
      "offerCount": availablePuppies
    }
  };

  return (
    <div className={`min-h-screen bg-[#0B0F1A] relative overflow-hidden pub-body ${fontTypo}`}>
      {/* SEO Meta Tags + AI Optimization (bezpośrednie wstrzykiwanie do <head>) */}
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        canonical={`${BASE_URL}/miot/${slug}`}
        ogType="product"
        ogImage={seoImage || `${BASE_URL}/logo-apisai.png`}
        ogImageWidth="1200"
        ogImageHeight="630"
        ogImageAlt={heroAlt}
        ogSiteName="ApisAI - Profesjonalne strony hodowli"
        robots="index, follow, max-image-preview:large"
        geoPlacename={litter.city || undefined}
        schemas={[
          schemaData,
          imageSchema,
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `Ile kosztuje szczeniak ${litter.breed}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": litter.price ? `Cena szczeniaka: ${priceDisplay}. Skontaktuj się z hodowcą aby potwierdzić dostępność.` : `Skontaktuj się z hodowcą aby poznać aktualną cenę szczeniąt.`
                }
              },
              {
                "@type": "Question",
                "name": `Ile szczeniąt jest dostępnych?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": availablePuppies > 0 ? `Aktualnie dostępnych jest ${availablePuppies} szczeniąt. Zarezerwuj szybko, bo szczenięta szybko znajdują nowe domy.` : `Aktualnie wszystkie szczenięta są zarezerwowane. Skontaktuj się aby zapytać o przyszłe mioty.`
                }
              },
              {
                "@type": "Question",
                "name": `Kiedy szczenięta będą gotowe do odbioru?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": readyDate ? `Szczenięta będą gotowe do odbioru od ${readyDate.toLocaleDateString('pl-PL')}.` : `Skontaktuj się z hodowcą aby ustalić termin odbioru.`
                }
              },
              {
                "@type": "Question",
                "name": `Gdzie znajduje się hodowla?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": litter.city ? `Hodowla znajduje się w ${litter.city}${litter.voivodeship ? `, woj. ${litter.voivodeship}` : ''}, Polska.` : `Skontaktuj się z hodowcą aby poznać lokalizację.`
                }
              }
            ]
          }
        ].filter(Boolean)}
      />

      {/* Noise overlay */}
      <div 
        className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}
      />

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <img src={lightboxImage} alt={heroAlt} className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* ============ HERO SECTION ============ */}
      <section className="relative min-h-[70vh] flex items-end">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src={heroImage}
            alt={heroAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F1A] via-[#0B0F1A]/70 to-[#0B0F1A]/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Title */}
            <p className="text-[#D4AF37] font-medium tracking-[0.3em] uppercase text-sm mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
              {isPlanned ? '🐾 Planowany miot' : '🐾 Szczenięta'}
            </p>

            <h1 
              className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-4"
              style={{ fontFamily: 'var(--pub-head)' }}
            >
              <GoldText>{litter.breed}</GoldText>
            </h1>

            {litter.name && (
              <p className="text-xl md:text-2xl text-gray-300 mb-6">{litter.name.toLowerCase().startsWith('miot') ? litter.name : `Miot ${litter.name}`}</p>
            )}

            {/* Date badge */}
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 mb-8">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-white">
                {isPlanned ? 'Planowane: ' : 'Urodzone: '}
                {birthDate ? birthDate.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Data do ustalenia'}
              </span>
            </div>

            {/* OLX-style view counter */}
            <div className="flex justify-center mb-6">
              <ViewCounter totalViews={totalViews} lastView={lastView} slug={slug} variant="detail" />
            </div>

            {/* Quick facts */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-8">
              {litter.city && (
                <div className="flex items-center gap-2 text-gray-300">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  <span>{litter.city}{litter.voivodeship ? `, ${litter.voivodeship}` : ''}</span>
                </div>
              )}
              {readyDate && (
                <div className="flex items-center gap-2 text-gray-300">
                  <Home className="w-5 h-5 text-[#D4AF37]" />
                  <span>Odbiór: {readyDate.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-300">
                <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                <span>{priceDisplay}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Baby className="w-5 h-5 text-[#D4AF37]" />
                <span>{availablePuppies} dostępnych</span>
              </div>
              {/* Status badge */}
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                litter.status === 'available' || !litter.status ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                litter.status === 'reserved' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                litter.status === 'planned' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                litter.status === 'sold' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              }`}>
                {litter.status === 'available' ? '✓ Dostępne' :
                 litter.status === 'reserved' ? '⏳ Rezerwacje' :
                 litter.status === 'planned' ? '📅 Planowany' :
                 litter.status === 'sold' ? '✗ Sprzedane' : '✓ Dostępne'}
              </div>
            </div>

            {/* Organization / FCI badges */}
            {(litter.is_fci || (litter.organization_type && litter.organization_type !== 'unknown' && litter.organization_type !== '')) && (
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {litter.is_fci && (
                  <span data-testid="litter-badge-fci" className="px-3 py-1 text-xs font-bold tracking-wider uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-full">
                    FCI
                  </span>
                )}
                {litter.organization_type && litter.organization_type !== 'unknown' && litter.organization_type !== '' && (
                  <span data-testid="litter-badge-org" className="px-3 py-1 text-xs font-medium tracking-wide bg-white/10 text-white/70 border border-white/20 rounded-full">
                    {litter.organization_type === 'zkwp_fci' ? 'ZKwP' : litter.organization_type === 'fci_other' ? 'Inna FCI' : litter.organization_type === 'khron' ? 'KHRON' : litter.organization_type === 'wush' ? 'WUSH' : litter.organization_type === 'other' ? (litter.organization_custom || 'Inna') : ''}
                  </span>
                )}
              </div>
            )}
            {litter.sire_external && litter.father_name && (
              <div className="flex justify-center mt-2">
                <span className="px-3 py-1 text-xs text-amber-400/70 bg-amber-500/10 border border-amber-500/20 rounded-full">
                  Reproduktor zewnętrzny: {litter.father_name}
                </span>
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                className="bg-gradient-to-r from-[#D4AF37] to-[#F5DEB3] text-[#0B0F1A] font-bold px-8 py-6 rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all transform hover:-translate-y-1 text-lg"
                onClick={() => setShowContactForm(true)}
                data-testid="contact-litter-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Zapytaj o szczeniaka
              </Button>
              {isPremium && (
                <Button 
                  variant="outline"
                  className="border-[#D4AF37] text-[#D4AF37] px-8 py-6 rounded-full hover:bg-[#D4AF37]/10 text-lg"
                  data-testid="reserve-litter-btn"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Zarezerwuj
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ PARENTS SECTION ============ */}
      {(father.name || mother.name) && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'var(--pub-head)' }}>
                Rodzice miotu
              </h2>
              <p className="text-gray-400 mb-12 text-center">Poznaj rodziców szczeniąt</p>

              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <ParentCard 
                  parent={father} 
                  gender="male" 
                  linkTo={litter.father_stud_slug ? `/reproduktor/${litter.father_stud_slug}` : null}
                  linkLabel="Strona reproduktora"
                />
                <ParentCard 
                  parent={mother} 
                  gender="female"
                  linkTo={litter.kennel_slug ? `/hodowla/${litter.kennel_slug}` : null}
                  linkLabel="Pokaż hodowlę"
                />
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============ INDIVIDUAL PUPPIES SECTION ============ */}
      {litter.puppies && litter.puppies.length > 0 && (
        <section className="py-20 bg-[#10182B]/30">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'var(--pub-head)' }}>
                Nasze szczeniaki
              </h2>
              <p className="text-gray-400 mb-10 text-center">
                {litter.puppies.filter(p => p.status === 'available').length} z {litter.puppies.length} dostępnych
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {litter.puppies.map((puppy, index) => (
                  <PuppyCard 
                    key={puppy.id || index}
                    puppy={puppy}
                    index={index}
                    isPremium={isPremium}
                    onReserve={(p) => {
                      setContactForm(prev => ({
                        ...prev,
                        message: `Chcę zarezerwować szczeniaka: ${p.name || `#${index + 1}`} (${p.sex === 'male' ? 'pies' : 'suczka'})`
                      }));
                      setShowContactForm(true);
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============ GALLERY ============ */}
      {litter.gallery && litter.gallery.length > 0 ? (
        <section className="py-20 bg-[#10182B]/30">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'var(--pub-head)' }}>
                    {readyDate ? `Gotowe do odbioru: ${readyDate.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}` : 'Galeria'}
                  </h2>
                  <p className="text-gray-400">Zdjęcia szczeniąt</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {litter.gallery.map((img, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className={`relative group cursor-pointer overflow-hidden rounded-xl ${
                      i === 0 ? 'col-span-2 row-span-2' : ''
                    }`}
                    onClick={() => setLightboxImage(getImageUrl(img))}
                  >
                    <img 
                      src={getImageUrl(img)}
                      alt={buildImageAlt({ kind: 'litter', name: litter.name, breed: litter.breed, city: litter.city, index: i })}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover aspect-square group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      ) : (
        <section className="py-20 bg-[#10182B]/30">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <EmptyState 
              icon={Camera}
              title="Galeria w przygotowaniu"
              description="Hodowca wkrótce doda zdjęcia szczeniąt"
            />
          </div>
        </section>
      )}

      {/* ============ ABOUT LITTER ============ */}
      {litter.description && (
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'var(--pub-head)' }}>
                O miocie
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">{litter.description}</p>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============ PRICING & AVAILABILITY ============ */}
      <section className="py-20 bg-[#10182B]/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow */}
            <div className="absolute inset-0 bg-[#D4AF37]/10 blur-3xl rounded-full" />
            
            <GlassCard className="relative border-[#D4AF37]/30 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#D4AF37]/10 mb-6">
                <Baby className="w-10 h-10 text-[#D4AF37]" />
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--pub-head)' }}>
                Cena i dostępność
              </h2>

              <p className="text-5xl md:text-6xl font-bold my-6">
                <GoldText>{priceDisplay}</GoldText>
              </p>

              <div className="flex items-center justify-center gap-2 text-gray-300 mb-8">
                <Baby className="w-5 h-5 text-emerald-400" />
                <span className="text-xl">{availablePuppies} szczeniąt dostępnych</span>
              </div>

              {/* What's included */}
              <div className="text-left border-t border-white/10 pt-6 mb-6">
                <h3 className="text-white font-semibold mb-4">W cenie:</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Metryka / rodowód
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Szczepienia i odrobaczenie
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Książeczka zdrowia + chip
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Wyprawka startowa
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Wsparcie hodowcy
                  </li>
                </ul>
              </div>

              <Button 
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F5DEB3] text-[#0B0F1A] font-bold px-10 py-6 rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all transform hover:-translate-y-1 text-lg"
                onClick={() => setShowContactForm(true)}
                data-testid="contact-pricing-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Zapytaj o szczeniaka
              </Button>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      {/* ============ CONTACT SECTION ============ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--pub-head)' }}>
              Zainteresowany szczeniakiem?
            </h2>
            <p className="text-gray-400 mb-10">Skontaktuj się z nami</p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              {/* Kennel info */}
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <User className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <div className="text-left">
                  <p className="text-white font-semibold">{litter.kennel_name || 'Hodowca'}</p>
                  {litter.contact_phone && (
                    <a href={`tel:${litter.contact_phone}`} className="text-gray-400 text-sm hover:text-[#D4AF37] flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {litter.contact_phone}
                    </a>
                  )}
                </div>
              </div>

              {/* CTAs */}
              <Button 
                className="bg-gradient-to-r from-[#D4AF37] to-[#F5DEB3] text-[#0B0F1A] font-bold px-8 py-4 rounded-full"
                onClick={() => setShowContactForm(true)}
              >
                <Mail className="w-5 h-5 mr-2" />
                Wyślij zapytanie
              </Button>
            </div>

            {/* Quick Contact Buttons - WhatsApp, Call, Share, QR */}
            {litter.contact_phone && (
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <WhatsAppButton 
                  phone={litter.contact_phone} 
                  message={`Dzień dobry! Interesuję się miotem ${litter.name} (${litter.breed}). Czy są jeszcze dostępne szczenięta?`}
                />
                <CallButton phone={litter.contact_phone} />
                <FacebookShareButton url={`${BASE_URL}/miot/${slug}`} title={`Szczenięta ${litter.breed}`} />
                <QRCodeButton url={`${BASE_URL}/miot/${slug}`} title={litter.name} />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ============ CONTACT FORM MODAL ============ */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#10182B] rounded-2xl border border-white/10 p-8 max-w-md w-full"
          >
            <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--pub-head)' }}>
              Zapytaj o szczeniaka
            </h3>
            <p className="text-gray-400 mb-6">Miot: {litter.name} ({litter.breed})</p>

            {litter.is_claimed === false ? (
              <div data-testid="litter-lead-blocked" className="text-sm text-white/70 leading-relaxed">
                <p className="mb-3">
                  Ta strona miotu to <strong className="text-white">nieoficjalna wizytówka</strong> utworzona na podstawie publicznie dostępnych danych. Formularz kontaktowy jest nieaktywny do czasu przejęcia profilu przez hodowcę.
                </p>
                <p className="mb-5">Jesteś hodowcą? Skorzystaj z opcji „Przejmij profil".</p>
                <Button type="button" variant="outline" className="w-full border-white/20" onClick={() => setShowContactForm(false)}>Zamknij</Button>
              </div>
            ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="litter-lead-form">
              <Input
                placeholder="Twoje imię *"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                className="bg-white/5 border-white/10"
                data-testid="litter-lead-name"
                required
              />
              <Input
                type="email"
                placeholder="Email *"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                className="bg-white/5 border-white/10"
                data-testid="litter-lead-email"
                required
              />
              <Input
                type="tel"
                placeholder="Telefon"
                value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                className="bg-white/5 border-white/10"
                data-testid="litter-lead-phone"
              />
              <textarea
                placeholder="Wiadomość (opcjonalnie)"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
                data-testid="litter-lead-message"
              />

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-white/20"
                  onClick={() => setShowContactForm(false)}
                >
                  Anuluj
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#D4AF37] hover:bg-[#B4941F] text-black"
                  disabled={submitting}
                  data-testid="litter-lead-submit"
                >
                  {submitting ? 'Wysyłanie...' : 'Wyślij'}
                </Button>
              </div>
            </form>
            )}
          </motion.div>
        </div>
      )}

      {/* ============ MOBILE STICKY CTA ============ */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#0B0F1A]/95 backdrop-blur-xl border-t border-white/10 p-4 safe-area-inset-bottom">
        <div className="flex gap-3">
          <Button 
            className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#F5DEB3] text-[#0B0F1A] font-bold py-5 rounded-full"
            onClick={() => setShowContactForm(true)}
            data-testid="mobile-contact-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Zapytaj
          </Button>
          {isPremium && (
            <Button 
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] py-5 rounded-full"
              data-testid="mobile-reserve-btn"
            >
              <Heart className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>
      
      {/* ApisAI System Footer */}
      <ApisAIFooter pageType="litter" pageSlug={slug} theme={footerTheme} />
      
      {/* Real View Counter */}
      <ViewCounterBadge pageType="litter" pageSlug={slug} />
    </div>
  );
}
